<script setup>
import { computed } from 'vue';

const props = defineProps({
  score: {
    type: Number,
    required: true,
    validator: value => value >= 0 && value <= 100,
  },
  factors: {
    type: Object,
    default: () => ({}),
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
  // 'pill' (default, inalterado) — número num badge colorido.
  // 'gauge' — medidor radial + número + badge, para lugares onde o score é o
  // dado principal (ex.: bloco "Informações do item" do card do pipeline).
  variant: {
    type: String,
    default: 'pill',
    validator: value => ['pill', 'gauge'].includes(value),
  },
});

const category = computed(() => {
  if (props.score >= 70) return 'hot';
  if (props.score >= 40) return 'warm';
  return 'cold';
});

const colorClasses = computed(() => {
  const colors = {
    hot: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
    warm: 'bg-n-amber-3 text-n-amber-11 border-n-amber-6',
    cold: 'bg-n-slate-3 text-n-slate-11 border-n-slate-6',
  };
  return colors[category.value];
});

// Cor de traço do gauge via `currentColor` — mesma paleta de `colorClasses`,
// só que aplicada como `text-*` para o SVG herdar em `stroke="currentColor"`.
const gaugeStrokeClass = computed(() => {
  const colors = {
    hot: 'text-n-ruby-9',
    warm: 'text-n-amber-9',
    cold: 'text-n-slate-8',
  };
  return colors[category.value];
});

const categoryLabel = computed(() => {
  const labels = {
    hot: 'HOT_LEAD',
    warm: 'WARM_LEAD',
    cold: 'COLD_LEAD',
  };
  return labels[category.value];
});

const factorsTooltip = computed(() => {
  if (!props.factors || Object.keys(props.factors).length === 0) return '';

  return Object.entries(props.factors)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n');
});

// ── Gauge geometry ────────────────────────────────────────────────────────────
const GAUGE_RADIUS = 26;
const gaugeCircumference = 2 * Math.PI * GAUGE_RADIUS;
const gaugeOffset = computed(
  () => gaugeCircumference * (1 - props.score / 100)
);
</script>

<template>
  <div v-if="variant === 'gauge'" class="inline-flex items-center gap-3">
    <svg
      class="-rotate-90 flex-shrink-0"
      width="60"
      height="60"
      viewBox="0 0 60 60"
      :title="factorsTooltip"
    >
      <circle
        cx="30"
        cy="30"
        :r="GAUGE_RADIUS"
        fill="none"
        stroke="currentColor"
        stroke-width="6"
        class="text-n-alpha-2"
      />
      <circle
        cx="30"
        cy="30"
        :r="GAUGE_RADIUS"
        fill="none"
        stroke="currentColor"
        stroke-width="6"
        stroke-linecap="round"
        class="transition-[stroke-dashoffset] duration-300"
        :class="gaugeStrokeClass"
        :stroke-dasharray="gaugeCircumference"
        :stroke-dashoffset="gaugeOffset"
      />
    </svg>
    <div>
      <p class="text-xl font-bold leading-none text-n-slate-12">
        {{ $t('PIPELINE.LEAD_SCORE.SCORE_OUT_OF_100', { score }) }}
      </p>
      <span
        :class="colorClasses"
        class="mt-1.5 inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold"
      >
        {{ $t(`PIPELINE.LEAD_SCORE.${categoryLabel}`) }}
      </span>
    </div>
  </div>

  <div v-else class="inline-flex items-center gap-2">
    <span
      :class="colorClasses"
      :title="factorsTooltip"
      class="px-2 py-1 rounded-full text-xs font-semibold border"
    >
      {{ score }}
    </span>
    <span v-if="showLabel" class="text-sm text-n-slate-11">
      {{ $t(`PIPELINE.LEAD_SCORE.${categoryLabel}`) }}
    </span>
  </div>
</template>
